"use client";

import type { LeadFormExitMethod } from "@homeinstead/analytics";
import type { FormEvent, ReactNode } from "react";
import type {
  LeadFlowBlockPresentation,
  LeadFlowDocument,
  LeadFlowOfficeSubmission,
  LeadFlowOfficeValues,
  LeadFlowSectionTakeover,
  LeadFlowSectionTheme,
  LeadFlowSubmission,
} from "./types";

import { useCallback, useMemo, useRef, useState } from "react";
import {
  EntryPreview,
  Launcher,
  LeadFlowRuntimeProvider,
  StepFrame,
  Surface,
  useLeadFlowAnswerStore,
} from "./components";
import { HoneypotField } from "./components/HoneypotField";
import { TimingTokenField } from "./components/TimingTokenField";
import { NO_STEPS } from "./constants";
import {
  useCopy,
  useInterstitialAutoAdvance,
  useLauncherFocus,
  useNavigation,
  useSectionTakeoverScroll,
  useStepDirection,
  useStepFocus,
  useStepTracking,
  useSubmission,
  useSubmissionTracking,
  useSurface,
  useTakeoverSectionMinHeight,
  useTimingTokenGate,
} from "./hooks";
import {
  getAnswerLabels,
  getQuestionStyle,
  getStepLayout,
  getStepThemeContrast,
  getStepTitle,
  getStepTransitionClassName,
  getStepUserSelection,
  resolveStepTheme,
} from "./utils";
import "./lead-flow-step-transition.css";

type LeadFlowProps = {
  blockKey: string;
  /**
   * Section content that leads the flow while its first step sits in the
   * section: under the progress indicator and above the step's items. It
   * gives way with the first step, so a takeover never shows it.
   */
  firstStepContent?: ReactNode;
  leadFlow: LeadFlowDocument;
  officeSubmission: LeadFlowOfficeSubmission;
  officeValues: LeadFlowOfficeValues;
  presentation: LeadFlowBlockPresentation;
  /**
   * When set, an inline flow's later steps take over the surrounding
   * section: past the first step the container carries
   * `data-lead-flow-takeover` set to the step's theme, which the section
   * paints and lays out against, and the section holds the tallest height it
   * has reached so it doesn't shrink between steps.
   */
  sectionTakeover?: LeadFlowSectionTakeover;
  /** The surrounding section's theme, which `inherit` steps take. */
  sectionTheme?: LeadFlowSectionTheme;
};

const THANK_YOU_TRANSITION_KEY = "lead-flow-thank-you";

function LeadFlowForm({
  blockKey,
  firstStepContent,
  leadFlow,
  officeSubmission,
  officeValues,
  presentation,
  sectionTakeover,
  sectionTheme,
}: LeadFlowProps) {
  const store = useLeadFlowAnswerStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const steps = leadFlow.steps ?? NO_STEPS;
  const [submission, setSubmission] = useState<LeadFlowSubmission | null>(null);
  const [formElement, setFormElement] = useState<HTMLFormElement | null>(null);
  // The current step owns whether an answer is pending, so updates never
  // re-render the flow while it is deferring a step change; Enter reads it.
  const isAnswerPendingRef = useRef(false);
  const copy = useCopy();

  const {
    advance,
    clearErrors,
    currentStep,
    currentStepIndex,
    errors,
    exitToFirstStep,
    goBack,
    goToStep,
    hasLoadedAnswers,
    isShowingThankYou,
    liveStepParameter,
    showErrors,
    stepParameter,
    validateCurrentStep,
  } = useNavigation({
    blockKey,
    leadFlow,
    thankYouStepParameter: submission?.thankYouStepParameter,
  });

  const { hasUnroutableError, isPending, state, submit } = useSubmission({
    blockKey,
    clearErrors,
    goToStep,
    leadFlow,
    liveStepParameter,
    officeSubmission,
    setSubmission,
    showErrors,
    stepParameter,
    submission,
  });

  const {
    closeLauncher,
    dialogSurface,
    isInContentSlot,
    isOnSurface,
    isOpen,
    isTakingOverSection,
    openLauncher,
    renderedSurface,
    takesOverSection,
  } = useSurface({
    currentStepIndex,
    isShowingThankYou,
    presentation,
    sectionTakeover,
    stepCount: steps.length,
  });

  const isLastStep = currentStepIndex === steps.length - 1;
  const { isAwaitingTimingToken, onTimingTokenReadyChange } =
    useTimingTokenGate(isLastStep);
  const isSubmitPending = isPending || isAwaitingTimingToken;

  const stepTheme = resolveStepTheme({
    isInContentSlot,
    isOnSurface,
    isTakingOverSection,
    sectionTakeover,
    theme: currentStep?.theme,
  });
  // A dialog sits outside the section, so its steps never inherit its theme.
  const themeContrast = getStepThemeContrast(
    stepTheme,
    isOnSurface ? undefined : sectionTheme,
  );
  const runtimeValues = useMemo(
    () => ({
      ...officeValues,
      answerLabels: getAnswerLabels(leadFlow),
      isSectionTakeover: isTakingOverSection,
      questionStyle: getQuestionStyle(leadFlow),
      stepTheme,
      themeContrast,
    }),
    [isTakingOverSection, leadFlow, officeValues, stepTheme, themeContrast],
  );

  useStepFocus(
    containerRef,
    currentStep?.id,
    hasLoadedAnswers && isOpen && !isShowingThankYou,
  );
  useLauncherFocus(containerRef, isOpen);

  const direction = useStepDirection(
    isShowingThankYou ? steps.length : currentStepIndex,
  );
  const transitionKey = isShowingThankYou
    ? THANK_YOU_TRANSITION_KEY
    : currentStep?.id;

  useSectionTakeoverScroll(containerRef, transitionKey, isTakingOverSection);
  useTakeoverSectionMinHeight(containerRef, {
    isHolding: isTakingOverSection,
    isMeasuring: takesOverSection && (isInContentSlot || isTakingOverSection),
  });

  // `step_name` is the step `id`, which is locked after publish, so it
  // survives label edits.
  const trackStep = useStepTracking({
    deliveryContext: leadFlow.deliveryContext,
    franchiseId: officeSubmission?.organizationNumber ?? 0,
    surface: renderedSurface,
    versionId: leadFlow.versionId,
  });
  useSubmissionTracking({
    officeSubmission,
    state,
    submittedAnswers: submission?.answers ?? null,
    surface: submission?.surface ?? renderedSurface,
    versionId: leadFlow.versionId,
  });

  // Every way forward (an answer that auto-advances, Continue, Enter, and an
  // interstitial's countdown) sends one Lead Form Step for the step it leaves.
  const advanceAndTrack = useCallback(() => {
    const leavingStep = currentStep;
    if (advance() && leavingStep) {
      trackStep.leadFormStep({
        stepName: leavingStep.id,
        userSelection: getStepUserSelection(leavingStep, store.getSnapshot()),
      });
    }
  }, [advance, currentStep, store, trackStep]);

  const stepContextValue = useMemo(
    () => ({
      advance: advanceAndTrack,
      errors,
      isAnswerPendingRef,
      // The in-page first step uses the subheading. A first step on a surface
      // uses the heading so the dialog can move focus to it.
      isFirstStep: isInContentSlot,
    }),
    [advanceAndTrack, errors, isInContentSlot],
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isAnswerPendingRef.current) {
      return;
    }

    // Enter in a text field submits the form on any step.
    if (!isLastStep) {
      advanceAndTrack();
      return;
    }

    if (!hasLoadedAnswers || isSubmitPending || !validateCurrentStep()) {
      return;
    }

    submit(event.currentTarget, renderedSurface);
  };

  useInterstitialAutoAdvance({
    advance: advanceAndTrack,
    currentStep,
    currentStepIndex,
    isEnabled: hasLoadedAnswers && isOpen && !isShowingThankYou && !isLastStep,
  });

  const closeSurface = useCallback(
    (exitMethod: LeadFormExitMethod) => {
      // The thank-you screen is recorded on the pending submission, so closing
      // mid-submit would drop a successful lead's confirmation.
      if (isPending) {
        return;
      }
      if (!isShowingThankYou && currentStep) {
        trackStep.leadFormExit({ exitMethod, stepName: currentStep.id });
      }
      closeLauncher();
      setSubmission(null);
      exitToFirstStep();
    },
    [
      closeLauncher,
      currentStep,
      exitToFirstStep,
      isPending,
      isShowingThankYou,
      trackStep,
    ],
  );

  if (!currentStep || !transitionKey) {
    return null;
  }

  const layout = getStepLayout({
    currentStep,
    currentStepIndex,
    isInContentSlot,
    isShowingThankYou,
    isTakingOverSection,
    presentation: leadFlow.presentation,
    steps,
    surface: presentation.surface,
  });
  // An interstitial before the first question sits at position 0.
  const stepTitle = isShowingThankYou
    ? copy.thankYouTitle
    : (getStepTitle(currentStep) ??
      copy.getStepProgressLabel(
        Math.max(layout.progress.position, 1),
        layout.progress.total,
      ));
  const content = (
    <StepFrame
      blockKey={blockKey}
      currentStep={currentStep}
      hasServerError={isLastStep && hasUnroutableError}
      header={isInContentSlot ? firstStepContent : undefined}
      isLastStep={isLastStep}
      isReady={hasLoadedAnswers}
      isShowingThankYou={isShowingThankYou}
      isSubmitPending={isSubmitPending}
      isSubmitting={isPending}
      isTakingOverSection={isTakingOverSection}
      layout={layout}
      onBack={currentStepIndex > 0 ? goBack : null}
      stepContext={stepContextValue}
      submission={submission}
      thankYou={leadFlow.thankYou}
      title={stepTitle}
      transitionClassName={getStepTransitionClassName(
        leadFlow.stepTransition,
        direction,
      )}
      transitionKey={transitionKey}
    />
  );
  const launcher = (
    <Launcher
      ctaLabel={presentation.ctaLabel}
      ctaSublabel={presentation.ctaSublabel}
      header={firstStepContent}
      onOpen={openLauncher}
      themeContrast={getStepThemeContrast("inherit", sectionTheme)}
    />
  );
  const [firstStep] = steps;

  return (
    <LeadFlowRuntimeProvider value={runtimeValues}>
      <div
        className="outline-none"
        data-lead-flow-block-key={blockKey}
        data-lead-flow-takeover={isTakingOverSection ? stepTheme : undefined}
        ref={containerRef}
        tabIndex={-1}
      >
        {isOnSurface && firstStep ? (
          <EntryPreview
            entry={presentation.entry}
            firstStep={firstStep}
            header={firstStepContent}
            isOnlyStep={steps.length === 1}
            launcher={launcher}
          />
        ) : null}
        {isOpen ? (
          <form noValidate onSubmit={handleSubmit} ref={setFormElement}>
            <HoneypotField />
            <TimingTokenField
              isLastStep={isLastStep}
              onReadyChange={onTimingTokenReadyChange}
            />
            {dialogSurface ? (
              formElement ? (
                <Surface
                  container={formElement}
                  onClose={closeSurface}
                  surface={dialogSurface}
                  theme={stepTheme}
                  title={stepTitle}
                >
                  {content}
                </Surface>
              ) : null
            ) : (
              content
            )}
          </form>
        ) : (
          launcher
        )}
      </div>
    </LeadFlowRuntimeProvider>
  );
}

/**
 * Renders nothing for a flow without steps. Must sit under
 * `LeadFlowAnswerStoreProvider`, which holds the page's answers.
 */
function LeadFlow(props: LeadFlowProps) {
  if (!props.leadFlow.steps?.length) {
    return null;
  }

  return <LeadFlowForm {...props} />;
}

export { LeadFlow };
